/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { UniversalTrackerBlueprintMin, UniversalTrackerPlain } from '../types/universalTracker';
import { getSDGShortTitle } from '../constants/sdg-data';
import { getGroup } from '@g17eco/core';
import { RootState } from '../reducers';
import { Document } from 'flexsearch';
import { SurveyActionData } from '../model/surveyData';
import { QuestionGroup } from '../types/survey';

const selectInitiativeBlueprintQuestions = (state: RootState) => state.initiativeBlueprintQuestions.data;
const selectBlueprintQuestions = (state: RootState) => state.blueprintQuestions.data;

export const generateFlexSearchMap = (blueprintQuestions: undefined | UniversalTrackerBlueprintMin[]) => {

  const indexSearch = new Document({
    tokenize: 'forward',
    preset: 'performance',
    document: {
      id: '_id',
      index: [
        'valueLabel',
        'name',
        'typeCode',
        'sdgTagText',
        'alternativeName',
        'alternativeGroupName',
        'alternativeTypeCode',
        'alternativeValueLabel',
        'tableColumns'
      ]
    }
  });

  (blueprintQuestions ?? []).forEach((utr) => {

    const tableColumns = utr.valueValidation?.table?.columns.map(column => column.name).join(' ') ?? '';
    const sdgTagText = utr.tags?.sdg?.map(code => `SDG ${code} ${getSDGShortTitle(code)}`).join(' ') ?? '';

    if (!utr.alternatives) {
      indexSearch.add({ ...utr, sdgTagText: sdgTagText, tableColumns: tableColumns })
      return;
    }

    const name = [];
    const groupName = [];
    const typeCode = [];
    const valueLabel = [];
    for (const [key, value] of Object.entries(utr.alternatives)) {
      const group = getGroup('standards', key);
      if (group) {
        name.push(group.name);
        groupName.push(value.name);
        typeCode.push(value.typeCode);
        valueLabel.push(value.valueLabel)
      }
    }

    indexSearch.add({
      ...utr,
      id: utr._id,
      alternativeName: name.join(' '),
      alternativeGroupName: groupName.join(' '),
      alternativeTypeCode: typeCode.join(' '),
      alternativeValueLabel: valueLabel.join(' '),
      sdgTagText: sdgTagText,
      tableColumns: tableColumns,
    })
  });

  return indexSearch
}

export const getInitiativeBlueprintFlexSearchMap = createSelector([selectInitiativeBlueprintQuestions], generateFlexSearchMap)

export const getBlueprintFlexSearchMap = createSelector([selectBlueprintQuestions], generateFlexSearchMap)

export const generateSurveyQuestionsFlexSearchMap = (survey?: {questionGroups: QuestionGroup[]}) => {
  if (!survey) {
    return generateFlexSearchMap([]);
  }

  const list = new Map<string, UniversalTrackerPlain>();
  survey.questionGroups.forEach((questionGroup) => {
    questionGroup.questions.forEach(({ utr }) => {
      list.set(utr._id, utr);
    });
  });
  const questionList = Array.from(list.values());

  return generateFlexSearchMap(questionList);
}

export const getSurveyFlexSearchMap = createSelector([(state) => state.survey], (surveyState) => {
  const surveyData: SurveyActionData | undefined = surveyState.loaded ? surveyState.data : undefined;

  return generateSurveyQuestionsFlexSearchMap(surveyData)
});
